import { client } from "@/client";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { CustomToastComponent } from "@/components/shared";
import { useMutation } from "@apollo/client/react";
import {
  CreateUserMutation,
  CreateUserMutationVariables,
  User,
  CreateUserDocument,
} from "@/graphql/__generated__/graphql";

/**
 * Custom hook for creating a user and handling the necessary cache updates.
 * @returns {Object} - Contains the createUserMutation function, loading state, and error state.
 */
export default function useCreateUserMutationHook(): {
  createUserMutation: any;
  loading: any;
  error: any;
  } {
  const { t } = useTranslation(["toasts"]);

  const [createUserMutation, { loading, error }] = useMutation<CreateUserMutation, CreateUserMutationVariables>(
    CreateUserDocument, {
      onCompleted: (data) => {
        toast(CustomToastComponent, {
          toastId: "user-created-success",
          data: {
            title: t("toasts.usercreatedsuccessfully"),
            type: "success",
          },
        });

        updateCache(data.createUser as User);
      },
    });

  /**
   * Adds reference of new User to cache.
   * @param {User} newUser The new user to be added.
   */
  const updateCache = (newUser: User) => {
    const userId = newUser.id;
    // Get the cache reference of the new User
    const newUserReference = client.cache.identify({
      __typename: "User",
      id: userId,
    });

    // Update the cache with the new User reference
    // This will add the new User to the paginatedUsers query data
    client.cache.modify({
      fields: {
        paginatedUsers(existingPaginatedUsers = {}) {
          const { edges = [], pageInfo } = existingPaginatedUsers;

          // Check if the user already exists in the cache to avoid duplicates
          const existingUserIds = edges.map((edge: any) => edge.node.id);
          if (existingUserIds.includes(userId)) {
            return existingPaginatedUsers;
          }

          // Add the new user reference to the edges field
          const newEdge = {
            __typename: "UserEdge",
            node: { __ref: newUserReference },
            cursor: `${userId}`,
          };
          return {
            ...existingPaginatedUsers,
            edges: [newEdge, ...edges],
            pageInfo: {
              ...pageInfo,
            },
          };
        },
      },
    });
  };

  return {
    createUserMutation,
    loading,
    error,
  };
}
